import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Box,
  TextField,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Img, TitlesWrapper } from './CheckYourEmail.styles';
import { PreOnboardingParams, SetupForm } from '../../PreOnboardingPage';

import { useEnrollDirectAccessPatient } from '../../../../hooks/useEnrollDirectAccessPatient';

import SleepioThumbsUp from '@/assets/SleepioThumbsUp.svg';
import LimbotHappy from '@/assets/LimbotHappy.svg';
import { isSpark as isSparkHelper } from '@/utils/productHelpers';
import {
  ActiveFeatureFlag,
  FeatureFlagProvider,
  useFeatureFlag,
} from '@/hooks/usePostHogFeatureFlags';
import Button from '@/components/Button';

type Props = {
  isGeneric?: boolean;
  onCommitFlagsEmail?: (email: string) => void;
};

const RESEND_COOLDOWN_MS = 15_000;

const CheckYourEmail: React.FC<Props> = ({ isGeneric = true, onCommitFlagsEmail }) => {
  const { t } = useTranslation();
  const { product } = useParams<PreOnboardingParams>();
  const isSpark = isSparkHelper(product);
  const theme = useTheme();
  const { watch, setValue, getFieldState } = useFormContext<SetupForm>();
  const email = watch('email');
  const phoneNumber = watch('phoneNumber');
  const marketingConsent = watch('marketingConsent');
  const { enroll } = useEnrollDirectAccessPatient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const resendCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const trimmedEmail = (email || '').trim();
  const emailInvalid = !!getFieldState('email').error;
  /* kept logic commented in case we need to implement the oganization naming again
   const { data: organizationNameData } = useQuery(QUERY_ORGANIZATION_BY_SLUG, {
      variables: { organizationSlug: slug },
    }); */

  const startResendCooldown = useCallback(() => {
    setIsResendDisabled(true);
    if (resendCooldownTimerRef.current) {
      clearTimeout(resendCooldownTimerRef.current);
    }
    resendCooldownTimerRef.current = setTimeout(() => {
      setIsResendDisabled(false);
    }, RESEND_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    startResendCooldown();
    return () => {
      if (resendCooldownTimerRef.current) {
        clearTimeout(resendCooldownTimerRef.current);
        resendCooldownTimerRef.current = null;
      }
    };
  }, [startResendCooldown]);

  const variant = useFeatureFlag(ActiveFeatureFlag.EMPLOYER_NAME_EXPERIMENT);
  const cachedVariantRef = useRef<typeof variant>(undefined);
  if (variant !== undefined) {
    cachedVariantRef.current = variant;
  }
  const effectiveVariant = cachedVariantRef.current ?? variant;

  const handleResend = useCallback(async () => {
    if (!trimmedEmail) {
      return;
    }
    startResendCooldown();
    setIsSubmitting(true);
    try {
      const res = await enroll({
        email: trimmedEmail,
        phoneNumber: phoneNumber || null,
        marketingConsent: !!marketingConsent,
      });
      if (res?.success) {
        onCommitFlagsEmail?.(trimmedEmail);
        setShowSuccess(true);
        setShowError(false);
      } else {
        setShowSuccess(false);
        setShowError(true);
      }
    } catch {
      setShowSuccess(false);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [trimmedEmail, enroll, phoneNumber, marketingConsent, onCommitFlagsEmail, startResendCooldown]);

  if (effectiveVariant === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  const productName = isSpark ? 'Spark' : 'Sleepio';
  const flowType = isGeneric ? 'generic' : 'specific';
  const firstTitle = t(`coverage.pre_onboarding.${flowType}.check_your_email_title`);
  const secondTitle = t(`coverage.pre_onboarding.${flowType}.check_your_email_second_title`);
  const firstDescription = t(
    `coverage.pre_onboarding.${flowType}.check_your_email_subtitle`,
    { product: productName },
  );
  const secondDescription = t(
    productName === 'Spark'
      ? `coverage.pre_onboarding.${flowType}.check_your_email_second_description_spark`
      : `coverage.pre_onboarding.${flowType}.check_your_email_second_description`,
  );
  const resendButtonText = t(`coverage.pre_onboarding.${flowType}.resend_email`);
  const errorMessage = t('coverage.pre_onboarding.generic.something_went_wrong');
  const resendDisabled = isResendDisabled || isSubmitting || !trimmedEmail || emailInvalid;
  const isButtonActive = !resendDisabled && !isSubmitting;
  const buttonStyle = isSpark
    ? {
      borderRadius: 100,
      ...(isButtonActive && {
        backgroundColor: '#077b65',
        color: '#FFFFFF',
      }),
    }
    : undefined;
  return (
    <>
      <Img
        alt={isSpark ? 'Spark Waving Limbot' : 'Sleepio Thumbsup'}
        style={{ width: '206px' }}
        src={isSpark ? LimbotHappy : SleepioThumbsUp}
      />
      <TitlesWrapper data-is-generic={isGeneric || undefined}>
        <Typography variant="h2">
          {firstTitle}
        </Typography>
        <Typography style={{ marginTop: 8, marginBottom: 16 }}>
          {firstDescription}
        </Typography>
        <Typography variant="h3">
          {secondTitle}
        </Typography>
        <Typography style={{ marginTop: 8, marginBottom: 16 }}>
          {secondDescription}
        </Typography>
        <Box display="flex" justifyContent="center" width="100%" mt={2}>
          <Box
            width="100%"
            borderRadius={12}
            bgcolor="grey.300"
            p={2.5}
          >
            <TextField
              data-testid="verification-email-input"
              label={t('coverage.pre_onboarding.generic.email_label')}
              required
              value={email || ''}
              onChange={(e) => {
                setValue('email', e.target.value, { shouldDirty: true, shouldValidate: true });
                setShowSuccess(false);
                setShowError(false);
              }}
              variant="outlined"
              fullWidth
              size="small"
              InputProps={{
                style: {
                  backgroundColor: theme.palette.common.white,
                },
              }}
            />
            {showSuccess && (
              <Box mt={1.5}>
                <Alert severity="success">
                  {firstDescription}
                </Alert>
              </Box>
            )}
            {showError && (
              <Box mt={1.5}>
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              </Box>
            )}
            <Box mt={1.5} />
            <Button
              variant="contained"
              role="button"
              data-testid="resend-verification-email-button"
              type="button"
              fullWidth
              color="primary"
              disabled={resendDisabled}
              rounded
              onClick={handleResend}
              loading={isSubmitting}
              style={buttonStyle}
            >
              {resendButtonText}
            </Button>
          </Box>
        </Box>
      </TitlesWrapper>
    </>
  );
};

export default (props: Omit<Props, 'onCommitFlagsEmail'>) => {
  const { watch } = useFormContext<SetupForm>();
  const [flagsEmail, setFlagsEmail] = useState<string>(() => watch('email') || '');

  return (
    <FeatureFlagProvider email={flagsEmail}>
      <CheckYourEmail
        {...props}
        onCommitFlagsEmail={setFlagsEmail}
      />
    </FeatureFlagProvider>
  );
};
